import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { SrModelVersionEnum } from '@enums/sr-model-version.enum';
import { RoleManagerHelper } from '../helpers/role-manager.helper';
import { CREATE_SERVICE_REQUEST_BASE_PATH } from 'src/app/pages/v2/create-service-request/properties/create-service-request-base-path.properties';
import { CREATE_SERVICE_REQUEST_FIRST_STEP_ROUTE } from 'src/app/pages/v2/create-service-request/properties/service-request-wizard-steps.properties';
import {
  INTERNAL_SERVICE_REQUEST_COPY_PATH,
  INTERNAL_SERVICE_REQUEST_CREATE_PATH,
  INTERNAL_SERVICE_REQUEST_EDIT_PATH,
} from 'src/app/pages/v2/service-request/properties/internal-service-request-routes.properties';

/**
 * Decides where an action on a service request leads, and performs the navigation.
 *
 * Opening an existing request depends on the `modelVersion` the backend reports, because records of
 * both models still exist and each has its own itinerary page. Creating, editing and copying always
 * go to the V2 pages; which one depends only on whether the user is internal or a customer.
 *
 * Every entry point navigates through this service rather than building paths of its own, so the
 * service request routes are known in exactly one place.
 */
@Injectable({ providedIn: 'root' })
export class SrRedirectService {
  private readonly router = inject(Router);

  private readonly roleManagerHelper = inject(RoleManagerHelper);

  /** True when the service request belongs to the V2 model. Missing/undefined is treated as legacy. */
  isV2(modelVersion: number | null | undefined): boolean {
    return modelVersion === SrModelVersionEnum.V2;
  }

  /**
   * Builds the itinerary route path for a service request in the correct UI version.
   * @param serviceRequestId service request id
   * @param modelVersion backend `modelVersion` (1 = legacy, 2 = V2)
   */
  private getServiceRequestPath(serviceRequestId: string, modelVersion: number | null | undefined): string {
    if (this.isV2(modelVersion)) {
      return `/v2/itinerary/${serviceRequestId}`;
    }

    return `/itinerary/${serviceRequestId}`;
  }

  /** Opens an existing service request in the correct UI version. */
  openServiceRequest(serviceRequestId: string, modelVersion: number | null | undefined): Promise<boolean> {
    return this.router.navigateByUrl(this.getServiceRequestPath(serviceRequestId, modelVersion));
  }

  /**
   * Opens the page on which a new service request is raised.
   *
   * The two audiences have had separate pages since the V2 wizard shipped: a customer raises a
   * request through the stepped wizard, an internal user through the single-page internal form.
   * The role decides, which is also what the route guards on both pages check.
   */
  createServiceRequest(): Promise<boolean> {
    if (this.roleManagerHelper.isCustomerRole()) {
      return this.router.navigate([CREATE_SERVICE_REQUEST_BASE_PATH, CREATE_SERVICE_REQUEST_FIRST_STEP_ROUTE]);
    }

    return this.router.navigate([INTERNAL_SERVICE_REQUEST_CREATE_PATH]);
  }

  /**
   * Opens an existing service request for editing.
   *
   * @param serviceRequestId - the record to edit
   */
  editServiceRequest(serviceRequestId: string): Promise<boolean> {
    return this.router.navigate([INTERNAL_SERVICE_REQUEST_EDIT_PATH, serviceRequestId]);
  }

  /**
   * Opens a new service request prefilled from an existing one.
   *
   * @param serviceRequestId - the record to copy from
   * @param isLinkedToSource - whether the copy must also be linked back to that record
   */
  copyServiceRequest(serviceRequestId: string, isLinkedToSource = false): Promise<boolean> {
    return this.router.navigate(
      [INTERNAL_SERVICE_REQUEST_COPY_PATH, serviceRequestId],
      isLinkedToSource ? { queryParams: { linkSr: true } } : {},
    );
  }
}
